"use client";

import { Term } from "@/components/ui/atoms/term";
import { MiniPiano } from "@/components/ui/molecules/mini-piano";
import {
  entryScale,
  entryTones,
  type ChordEntry,
} from "@/lib/music/chord-catalog";
import { useNoteLabel } from "@/hooks/use-notation";
import { ROOT_PITCH_CLASS } from "@/lib/music/theory-helpers";
import {
  BORDER,
  FONT_MONO,
  SURFACE_LO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "./palette";
import {
  INTERVAL_FORMULA,
  TONE_LABEL,
  intervalDegree,
} from "./pedagogy-panel-helpers";
import { PedagogyPanelSectionTitle } from "./pedagogy-panel-section-title";

type Props = {
  entry: ChordEntry;
};

export function PedagogyPanelTheoryTab({ entry }: Props) {
  const tones = entryTones(entry);
  const scale = entryScale(entry);

  // Hero "construction" piano: root inversion at C4 anchor for clarity.
  const rootPc = ROOT_PITCH_CLASS[entry.root];
  const heroLowMidi = rootPc <= 4 ? 60 : 60 - 12;
  const heroMidis = entry.intervals.map((iv) => {
    const pc = (rootPc + iv) % 12;
    let m = heroLowMidi;
    while (m % 12 !== pc) {
      m++;
    }

    return m;
  });

  return (
    <div>
      <FormulaSection entry={entry} />
      <ConstructionSection
        heroMidis={heroMidis}
        heroLowMidi={heroLowMidi}
        preferFlats={entry.root.includes("b")}
        tones={tones}
      />
      {(entry.quality === "major" || entry.quality === "minor") && (
        <WhyShapeSection />
      )}
      <ScaleSection scale={scale} />
    </div>
  );
}

function FormulaSection({ entry }: { entry: ChordEntry }) {
  return (
    <section style={{ marginBottom: 22 }}>
      <PedagogyPanelSectionTitle>Formule</PedagogyPanelSectionTitle>
      <div
        style={{
          padding: "14px 16px",
          background: "rgba(139, 92, 246, 0.06)",
          border: `1px solid ${VIOLET_GLOW}`,
          borderRadius: 10,
          fontFamily: FONT_MONO,
          fontSize: 16,
          color: TEXT,
          letterSpacing: "0.05em",
          textAlign: "center",
          fontWeight: 500,
        }}
      >
        {entry.intervals.map((iv, i) => {
          const f = INTERVAL_FORMULA[iv];

          return (
            <span key={i}>
              {i > 0 && (
                <span style={{ margin: "0 8px", color: TEXT_DIM }}>, </span>
              )}
              {f ? (
                <>
                  {f.degree}
                  {f.sup && <sup style={{ fontSize: 10 }}>{f.sup}</sup>}
                </>
              ) : (
                iv
              )}
            </span>
          );
        })}
      </div>
      <p
        style={{
          fontSize: 12,
          color: TEXT_DIM,
          lineHeight: 1.6,
          marginTop: 10,
        }}
      >
        {entry.quality === "major" && (
          <>
            Un accord majeur empile une{" "}
            <Term termKey="tierce">tierce majeure</Term> (4 demi-tons) puis une
            tierce mineure (3 demi-tons) sur la{" "}
            <Term termKey="tonique">tonique</Term>.
          </>
        )}
        {entry.quality === "minor" && (
          <>
            Un accord mineur empile une{" "}
            <Term termKey="tierce">tierce mineure</Term> (3 demi-tons) puis une
            tierce majeure (4 demi-tons) sur la{" "}
            <Term termKey="tonique">tonique</Term>.
          </>
        )}
        {entry.quality !== "major" && entry.quality !== "minor" && (
          <>
            Empile les intervalles ci-dessus à partir de la{" "}
            <Term termKey="tonique">tonique</Term> pour construire l'accord.
          </>
        )}
      </p>
    </section>
  );
}

type ConstructionProps = {
  heroMidis: number[];
  heroLowMidi: number;
  preferFlats: boolean;
  tones: ReturnType<typeof entryTones>;
};

function ConstructionSection({
  heroMidis,
  heroLowMidi,
  preferFlats,
  tones,
}: ConstructionProps) {
  const toNoteLabel = useNoteLabel();

  return (
    <section style={{ marginBottom: 22 }}>
      <PedagogyPanelSectionTitle>Construction</PedagogyPanelSectionTitle>
      <div
        style={{
          padding: "16px 14px",
          background: SURFACE_LO,
          border: `1px solid ${BORDER}`,
          borderRadius: 10,
        }}
      >
        <MiniPiano
          midis={heroMidis}
          lowMidi={heroLowMidi - (heroLowMidi % 12)}
          octaves={2}
          highlightColor={VIOLET_BRIGHT}
          preferFlats={preferFlats}
          showLabels
          className="block h-[70px] w-full"
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 14,
            padding: "0 4px",
            gap: 8,
          }}
        >
          {tones.map((t, i) => (
            <div key={i} style={{ textAlign: "center", flex: 1 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 500,
                  color: TEXT,
                  fontFamily: FONT_MONO,
                }}
              >
                {intervalDegree(t.interval)}
              </div>
              <div style={{ fontSize: 11, color: TEXT_DIM, marginTop: 4 }}>
                {toNoteLabel(t.name)}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: TEXT_MUTE,
                  marginTop: 2,
                  fontFamily: FONT_MONO,
                }}
              >
                {TONE_LABEL[t.interval] ?? t.toneLabel}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyShapeSection() {
  return (
    <section style={{ marginBottom: 22 }}>
      <PedagogyPanelSectionTitle>Pourquoi cette forme ?</PedagogyPanelSectionTitle>
      <div style={{ fontSize: 12, color: TEXT_DIM, lineHeight: 1.7 }}>
        La <Term termKey="quinte">quinte juste</Term> donne la stabilité
        harmonique. La tierce détermine la couleur, majeure (joyeuse) ou
        mineure (mélancolique).
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            background: "rgba(255,255,255,0.03)",
            borderRadius: 8,
            borderLeft: `2px solid ${VIOLET}`,
          }}
        >
          <strong style={{ color: TEXT, fontWeight: 500 }}>À retenir.</strong>{" "}
          Majeur = tierce majeure. Mineur = tierce mineure. La quinte ne change
          pas.
        </div>
      </div>
    </section>
  );
}

type ScaleProps = {
  scale: ReturnType<typeof entryScale>;
};

function ScaleSection({ scale }: ScaleProps) {
  const toNoteLabel = useNoteLabel();

  return (
    <section>
      <PedagogyPanelSectionTitle>Dans la gamme</PedagogyPanelSectionTitle>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${scale.length}, 1fr)`,
          gap: 4,
          fontFamily: FONT_MONO,
          fontSize: 11,
        }}
      >
        {scale.map((d) => (
          <div
            key={d.roman}
            style={{
              padding: "10px 0",
              textAlign: "center",
              borderRadius: 6,
              background: d.isChordTone ? VIOLET_SOFT : "rgba(255,255,255,0.03)",
              border: `1px solid ${d.isChordTone ? VIOLET_GLOW : BORDER}`,
              color: d.isChordTone ? VIOLET_BRIGHT : TEXT_MUTE,
              fontWeight: d.isChordTone ? 600 : 400,
            }}
          >
            <div style={{ fontSize: 10, opacity: 0.6 }}>{d.roman}</div>
            <div style={{ marginTop: 2 }}>{toNoteLabel(d.name)}</div>
          </div>
        ))}
      </div>
      <p
        style={{
          fontSize: 11,
          color: TEXT_MUTE,
          marginTop: 10,
          lineHeight: 1.6,
        }}
      >
        L'accord utilise les <Term termKey="degre">degrés</Term> mis en
        évidence de la gamme.
      </p>
    </section>
  );
}
